import { useEffect, useState, type FormEvent } from 'react';
import { LayoutFrame, registerHomeTile, type APIResponse, useSessionStore } from '@room/shared-ui';
import { api } from '../lib/api';
import './SettingsPage.css';

registerHomeTile({
  id: 'home-settings',
  label: 'Settings',
  icon: '⚙️',
  route: '/settings',
  section: 'dock',
  order: 30,
});

type SettingsPayload = {
  settings: Record<string, unknown>;
  updatedAt: number | null;
};

type UserSettings = {
  displayName?: string;
  theme?: 'light' | 'dark';
  notificationsEnabled?: boolean;
};

export function SettingsPage() {
  const [formState, setFormState] = useState<UserSettings>({
    displayName: '',
    theme: 'light',
    notificationsEnabled: true,
  });
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const userRole = useSessionStore((state) => state.user?.role ?? null);

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.get<APIResponse<SettingsPayload>>('/settings');
        if (!response.success) {
          throw new Error(response.error || 'Failed to load settings');
        }
        const payload = (response.data?.settings ?? {}) as UserSettings;
        setFormState({
          displayName: payload.displayName ?? '',
          theme: payload.theme ?? 'light',
          notificationsEnabled: payload.notificationsEnabled ?? true,
        });
        setUpdatedAt(response.data?.updatedAt ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    };

    void loadSettings();
  }, []);

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSaving(true);
    setError(null);
    setMessage(null);

    try {
      const response = await api.post<APIResponse<SettingsPayload>>('/settings', {
        settings: formState,
        updatedAt: updatedAt ?? Date.now(),
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to save settings');
      }
      setUpdatedAt(response.data?.updatedAt ?? null);
      const nextSettings = (response.data?.settings ?? formState) as UserSettings;
      setFormState({
        displayName: nextSettings.displayName ?? '',
        theme: nextSettings.theme ?? 'light',
        notificationsEnabled: nextSettings.notificationsEnabled ?? true,
      });
      setMessage(response.message || 'Settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const formattedRole = userRole
    ? userRole.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    : 'User';

  return (
    <LayoutFrame narrow>
      <div className="section-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your personal preferences.</p>
      </div>

      {isLoading && <p className="loading-text">Loading settings…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {!isLoading && (
        <div className="content-card">
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label className="form-label" htmlFor="display-name">Display name</label>
              <input
                id="display-name"
                className="form-input"
                type="text"
                value={formState.displayName}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, displayName: event.target.value }))
                }
                placeholder="Enter display name"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="user-role">Role</label>
              <input
                id="user-role"
                className="form-input"
                type="text"
                value={formattedRole}
                readOnly
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="theme">Theme</label>
              <select
                id="theme"
                className="form-select"
                value={formState.theme}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, theme: event.target.value as 'light' | 'dark' }))
                }
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formState.notificationsEnabled ?? true}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, notificationsEnabled: event.target.checked }))
                  }
                />
                <span>Enable notifications</span>
              </label>
            </div>

            <button className="btn btn-primary btn-block" type="submit" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save settings'}
            </button>
          </form>
        </div>
      )}
    </LayoutFrame>
  );
}
